/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Cart Slice - Redux state management for shopping cart
 */

import { CartCoupon, CartProduct, StripeProduct } from "@/lib/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DOCUMENT_TRANSLATOR_PRODUCT_ID } from "@/lib/document-translator-pricing";

/**
 * Document Translator should never be persisted to localStorage because
 * it uses dynamic per-word pricing from URL query params. Restoring it
 * from storage would lose the word count context.
 */
export const EXCLUDED_STORAGE_PRODUCT_ID = DOCUMENT_TRANSLATOR_PRODUCT_ID;

/**
 * Restore persisted coupon from localStorage.
 * Coupon is cleared when cart is cleared or coupon is explicitly removed.
 */
const getPersistedCoupon = (): CartCoupon | null => {
  const stored = localStorage.getItem("cartCoupon");
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed.id === "string" && typeof parsed.name === "string") {
      return parsed as CartCoupon;
    }
  } catch {
    localStorage.removeItem("cartCoupon");
  }
  return null;
};

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartItems: [] as CartProduct[],
    tax: 0,
    // FCR S260 §3.15 (Sunil OPTION A, 22 Apr 2026): `taxAmountUsd`, `taxRate`,
    // and `taxCalculating` have been removed. The cart no longer renders a
    // pre-checkout tax estimate — Stripe Checkout's automatic_tax is the
    // single source of truth. The legacy `tax` rate-multiplier field above is
    // intentionally retained at 0 for any downstream consumer that still
    // reads it; no UI surfaces it.
    clearcart: false,
    coupon: getPersistedCoupon(),
    // BUG-1 (FCR Pass 11) integration fix: store the list of product IDs the
    // active coupon applies to so CartProductRow can access it without prop-
    // drilling through cart-details. null = no restriction (applies to all).
    couponApplicableProducts: null as string[] | null,
  },
  reducers: {
    updateTax(state, action: PayloadAction<number>) {
      state.tax = action.payload;
    },
    addCartItems(state, action: PayloadAction<CartProduct[]>) {
      state.cartItems = action.payload;
      const itemsToStore = state.cartItems.filter(
        (item: CartProduct) => item.id !== EXCLUDED_STORAGE_PRODUCT_ID,
      );
      localStorage.setItem("cartItems", JSON.stringify(itemsToStore));
    },

    addProductsToCart(state, action: PayloadAction<StripeProduct[]>) {
      action.payload.forEach((product: StripeProduct) => {
        const existingProduct = state.cartItems.find(
          (item: CartProduct) => item.id === product.id,
        );
        if (existingProduct) {
          // Product already exists in cart - DO NOT increment quantity
          // This prevents quantity from increasing on page refresh
          // when product is re-added from URL params
          // Skip special product, and skip incrementing for existing products
          if (product.id !== EXCLUDED_STORAGE_PRODUCT_ID) {
            // Only update if explicitly requested via fromUrlParams flag
            // Otherwise, keep existing quantity unchanged
            if (product.fromUrlParams) {
              // DD-8 / SK-U Issue 6: When product comes from URL (a price switch),
              // update price data so the cart shows the correct price/priceId — not stale values.
              // Previously this returned early with no update, causing stale price display.
              existingProduct.default_price = product.default_price;
              if (product.priceId !== undefined) {
                (existingProduct as StripeProduct & { priceId?: string }).priceId = product.priceId;
              }
              existingProduct.totalPrice = product.totalPrice;
              existingProduct.price = product.price;
              return;
            }
            // For non-URL additions (manual add), increment quantity
            existingProduct.quantity += product.quantity || 1;
          }
        } else {
          // Add new product with quantity (default to 1 if not set)
          const newProduct = {
            ...product,
            quantity: product.quantity || 1,
          };
          state.cartItems.push(newProduct);
        }
      });
      const itemsToStore = state.cartItems.filter(
        (item: CartProduct) => item.id !== EXCLUDED_STORAGE_PRODUCT_ID,
      );
      localStorage.setItem("cartItems", JSON.stringify(itemsToStore));
    },
    updateCartItems(state, action: PayloadAction<CartProduct[]>) {
      state.cartItems = action.payload;
      const itemsToStore = state.cartItems.filter(
        (item: CartProduct) => item.id !== EXCLUDED_STORAGE_PRODUCT_ID,
      );
      if (state.cartItems.length !== 0)
        localStorage.setItem("cartItems", JSON.stringify(itemsToStore));
    },
    clearCart(state) {
      state.cartItems = [];
      localStorage.setItem("cartItems", JSON.stringify([]));
      state.coupon = null;
      localStorage.removeItem("cartCoupon");
      state.clearcart = true;
      state.couponApplicableProducts = null;
    },
    applyCoupon(state, action: PayloadAction<CartCoupon>) {
      state.coupon = action.payload;
      localStorage.setItem("cartCoupon", JSON.stringify(action.payload));
    },
    clearCoupon(state) {
      state.coupon = null;
      localStorage.removeItem("cartCoupon");
      state.couponApplicableProducts = null;
    },
    /**
     * BUG-1 (FCR Pass 11) integration fix: store which product IDs the active
     * coupon applies to in Redux so CartProductRow can read it without
     * prop-drilling. Dispatched by useCoupon after a successful validation.
     * null = coupon applies to everything (unrestricted).
     */
    setCouponApplicableProducts(
      state,
      action: PayloadAction<string[] | null>,
    ) {
      state.couponApplicableProducts = action.payload;
    },
    /**
     * Update a single cart item by ID - prevents full cartItems array replacement
     * which would cause all CartProductRow components to re-render.
     *
     * Use this for targeted updates (e.g., quantity changes from URL params)
     * instead of updateCartItems when only one item needs modification.
     */
    updateSingleCartItem(
      state,
      action: PayloadAction<{ id: string; updates: Partial<CartProduct> }>,
    ) {
      const { id, updates } = action.payload;
      const index = state.cartItems.findIndex((item: CartProduct) => item.id === id);
      if (index !== -1) {
        state.cartItems[index] = { ...state.cartItems[index], ...updates };
        // Persist to localStorage (excluding special products)
        const itemsToStore = state.cartItems.filter(
          (item: CartProduct) => item.id !== EXCLUDED_STORAGE_PRODUCT_ID,
        );
        localStorage.setItem("cartItems", JSON.stringify(itemsToStore));
      }
    },
  },
});

export const {
  updateTax,
  addCartItems,
  addProductsToCart,
  updateCartItems,
  updateSingleCartItem,
  clearCart,
  applyCoupon,
  clearCoupon,
  setCouponApplicableProducts,
} = cartSlice.actions;
export default cartSlice.reducer;
